import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { geofenceService } from '../services/GeofenceService';
import './SettingsScreen.css';

interface GeofenceConfig {
  center: {
    latitude: number;
    longitude: number;
  };
  radius: number;
}

const SettingsScreen: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<GeofenceConfig>({
    center: { latitude: 12.9716, longitude: 77.5946 },
    radius: 100
  });
  const [currentLocation, setCurrentLocation] = useState<GeolocationPosition | null>(null);

  useEffect(() => {
    loadCurrentConfig();
    getCurrentLocation();
  }, []);

  const loadCurrentConfig = () => {
    const savedConfig = geofenceService.getGeofenceConfig();
    setConfig(savedConfig);
  };

  const getCurrentLocation = async () => {
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setCurrentLocation(position);
          },
          (error) => {
            console.error('Error getting current location:', error);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      }
    } catch (error) {
      console.error('Error accessing location:', error);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setConfig(prev => ({
      ...prev,
      [field]: typeof value === 'number' ? value : parseFloat(value) || 0
    }));
  };

  const handleCenterChange = (field: 'latitude' | 'longitude', value: string) => {
    setConfig(prev => ({
      ...prev,
      center: {
        ...prev.center,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const saveConfig = () => {
    geofenceService.updateGeofenceConfig(config);
    alert('Settings saved successfully!');
  };

  const resetToDefault = () => {
    if (window.confirm('Are you sure you want to reset to default settings?')) {
      geofenceService.resetToDefault();
      loadCurrentConfig();
      alert('Settings reset to default!');
    }
  };

  const useCurrentLocation = () => {
    if (currentLocation) {
      setConfig(prev => ({
        ...prev,
        center: {
          latitude: currentLocation.coords.latitude,
          longitude: currentLocation.coords.longitude
        }
      }));
    } else {
      alert('Current location not available. Please ensure location permissions are granted.');
    }
  };

  return (
    <div className="settings-screen">
      <div className="header">
        <h1>Settings</h1>
        <div className="nav-buttons">
          <button className="nav-button" onClick={() => navigate('/')}>
            🏠 Home
          </button>
        </div>
      </div>

      <div className="main-content">
        <div className="settings-card">
          <h2>Geofence Configuration</h2>
          <p className="settings-description">
            Configure the designated area for attendance tracking. The system will only allow attendance marking when within this area.
          </p>

          <div className="form-group">
            <label className="form-label">Center Latitude:</label>
            <input
              type="number"
              step="any"
              value={config.center.latitude}
              onChange={(e) => handleCenterChange('latitude', e.target.value)}
              className="form-input"
              placeholder="Enter latitude"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Center Longitude:</label>
            <input
              type="number"
              step="any"
              value={config.center.longitude}
              onChange={(e) => handleCenterChange('longitude', e.target.value)}
              className="form-input"
              placeholder="Enter longitude"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Radius (meters):</label>
            <input
              type="number"
              value={config.radius}
              onChange={(e) => handleInputChange('radius', parseInt(e.target.value) || 0)}
              className="form-input"
              placeholder="Enter radius in meters"
              min="10"
              max="10000"
            />
          </div>

          <div className="location-actions">
            <button className="btn btn-secondary" onClick={useCurrentLocation}>
              📍 Use Current Location
            </button>
            <button className="btn btn-primary" onClick={saveConfig}>
              💾 Save Settings
            </button>
            <button className="btn btn-danger" onClick={resetToDefault}>
              🔄 Reset to Default
            </button>
          </div>
        </div>

        <div className="settings-card">
          <h2>Current Location</h2>
          {currentLocation ? (
            <div className="location-info">
              <p><strong>Latitude:</strong> {currentLocation.coords.latitude.toFixed(6)}</p>
              <p><strong>Longitude:</strong> {currentLocation.coords.longitude.toFixed(6)}</p>
              <p><strong>Accuracy:</strong> {currentLocation.coords.accuracy?.toFixed(2)}m</p>
              <p><strong>Timestamp:</strong> {new Date(currentLocation.timestamp).toLocaleString()}</p>
            </div>
          ) : (
            <p>Location not available. Please ensure location permissions are granted.</p>
          )}
        </div>

        <div className="settings-card">
          <h2>System Information</h2>
          <div className="system-info">
            <p><strong>App Version:</strong> 1.0.0</p>
            <p><strong>Browser:</strong> {navigator.userAgent}</p>
            <p><strong>Geolocation Support:</strong> {navigator.geolocation ? '✅ Available' : '❌ Not Available'}</p>
            <p><strong>Local Storage:</strong> {localStorage ? '✅ Available' : '❌ Not Available'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsScreen; 